<template>
  <TaskModal
    ref="taskModal"
    :visible="visibleActionModal"
    :event="event"
    :task="task"
    create-mode
    @update:visible="$emit('update:visibleActionModal', $event)"
    @ok="save"
    @show="modalShow"
    @hide="$emit('update:visibleActionModal', false)"
  >
    <CommentUserForTaskModal v-model="commentUserId" :disabled="readOnly" />
  </TaskModal>
</template>

<script setup lang="ts">
import { computed, nextTick, type PropType, ref, inject } from "vue";
import {
  type EventListEventFieldsFragment,
  EventType,
  type ICommonTask,
  useCreateTaskMutation,
} from "@/graphql";
import TaskModal from "./TaskModal.vue";
import { addAction } from "@/graphql/apolloCacheUtils";
import CommentUserForTaskModal from "./CommentUserForTaskModal.vue";
import {
  BoardIdInjectionKey,
  CommentUserIdInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import { defaultTask as defaultTaskFunction, type EditTaskType } from "../helper/DefaultTask";
import store from "@/store";
import type { EventEditType } from "../../event/modal/EventEditType";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { hasRight } from "@/functions/authorizationState";

const props = defineProps({
  visibleActionModal: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment | EventEditType>,
    required: true,
  },
  defaultTask: {
    type: Object as PropType<EditTaskType>,
    required: false,
    default: null,
  },
});
const { siteId } = useProvidedBoardContext();
const readOnly = computed(
  () => props.event.deletedAt != null || !hasRight("sqdc.write", siteId.value),
);
const emit = defineEmits(["save", "update:visibleActionModal"]);
const task = ref(defaultTaskFunction());
const taskModal = ref<null | InstanceType<typeof TaskModal>>(null);
const { mutate: createTask } = useCreateTaskMutation({});
const eventType = computed(() => {
  switch (String(props.event.__typename)) {
    case "IHMaintenance":
      return EventType.IHMaintenance;
    case "IHFailureNotice":
      return EventType.IHFailureNotice;
    case "Issue":
      return EventType.Issue;
    case "HLPIdea":
      return EventType.HLPIdea;
    case "AuditResult":
      return EventType.AuditResult;
    case "QualityAssurance":
      return EventType.QualityAssurance;
    default:
      throw new Error("unknown event type CreateTaskModal");
  }
});
const defaultTaskFromShow = ref<EditTaskType | undefined>();
const commentUserId = inject(CommentUserIdInjectionKey, ref<number>());
const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));

function show(defaultTaskProp?: EditTaskType) {
  defaultTaskFromShow.value = defaultTaskProp;
  emit("update:visibleActionModal", true);
}
defineExpose({
  show,
});

function modalShow() {
  task.value = defaultTaskFromShow.value ?? props.defaultTask ?? defaultTaskFunction();
  commentUserId.value = store.getters["login/isAnonym"]
    ? store.getters["taskComment/getTaskCommentatorId"]
    : store.getters["login/userId"];
}
async function save() {
  if (readOnly.value) return;
  await nextTick();
  emit("update:visibleActionModal", false);
  emit("save");
  await nextTick();
  delete task.value.Folder;
  if (props.event.id == null) {
    throw Error("eventId missing. can't create task");
  }
  await createTask(
    {
      eventId: props.event.id,
      boardId: boardId.value ?? undefined,
      eventType: eventType.value,
      task: {
        ...(task.value as ICommonTask),
      },
      commentUserId: commentUserId.value as number,
    },
    {
      update: (cache, { data }) => {
        const storedObject = cache.identify({
          __typename: props.event.__typename,
          id: props.event.id,
        });
        if (storedObject && data != null) {
          addAction(storedObject, data?.createCommonTask);
        }
      },
    },
  );
}
</script>

<style lang="scss" scoped></style>
